import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, CircularProgress } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import { LineChart } from '@mui/x-charts/LineChart';
import api from '../../services/api';
import DeltaChip from './DeltaChip';

const RANGE_LABEL = { '24h': 'últimas 24h', '7d': 'últimos 7 dias', '30d': 'últimos 30 dias' };

/**
 * Timeline de sessões RADIUS no range selecionado.
 * Lê /dashboard/driade/sessions_timeline?range=24h|7d|30d.
 */
export default function SessionsTimelineCard({ range = '24h' }) {
    const [state, setState] = useState({ loading: true, buckets: [], total: 0, prev_total: 0, delta_pct: 0 });

    useEffect(() => {
        setState((s) => ({ ...s, loading: true }));
        api.get(`/dashboard/driade/sessions_timeline?range=${range}`)
            .then((res) => setState({ ...res.data, loading: false }))
            .catch(() => setState((s) => ({ ...s, loading: false })));
    }, [range]);

    const data = state.buckets.map((b) => b.count);
    const labels = state.buckets.map((b) => formatLabel(b.hour, range));

    return (
        <Paper
            elevation={2}
            sx={{
                m: 2,
                p: 2,
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '4px solid #e01b29',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon color="primary" />
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Sessões RADIUS — {RANGE_LABEL[range]}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {state.total.toLocaleString('pt-BR')}
                    </Typography>
                    <DeltaChip value={state.delta_pct} />
                </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1 }}>
                Período anterior: {state.prev_total.toLocaleString('pt-BR')} sessões
            </Typography>

            {state.loading ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : state.total === 0 && state.prev_total === 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
                    <Typography variant="body2" color="text.secondary">
                        Sem sessões registradas no período
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, minHeight: 180 }}>
                    <LineChart
                        height={180}
                        series={[{
                            data,
                            label: 'Sessões',
                            color: '#e01b29',
                            area: true,
                            showMark: range === '24h',
                            curve: 'monotoneX',
                        }]}
                        xAxis={[{ data: labels, scaleType: 'point' }]}
                        margin={{ left: 40, right: 16, top: 16, bottom: 32 }}
                        sx={{
                            '& .MuiLineElement-root': { strokeWidth: 2 },
                            '& .MuiAreaElement-root': { fillOpacity: 0.18 },
                        }}
                        slotProps={{ legend: { hidden: true } }}
                    />
                </Box>
            )}
        </Paper>
    );
}

function formatLabel(iso, range) {
    const d = new Date(iso);
    if (range === '24h') return `${String(d.getHours()).padStart(2, '0')}h`;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}
